import { ActionTree } from 'vuex';
import { IPublishDocumentPayload, ISetDocumentRevisionStatusPayload } from '../types';
import { DocumentService } from '@/api/documents/services/document.service';
import {
  mutationSetDocument,
  mutationSetPages,
  mutationSetDocumentRevisions,
  mutationSetDocumentRevisionStatus,
  mutationSetFields,
  mutationSetLoaded,
  mutationSetLoading,
  mutationSetPageWidth,
  mutationSetRevisionsLoading,
  mutationSetUpdateFailed,
} from '@/dealroom-view/store/dealroom-view.mutations';
import { IDealroomViewState } from '@/dealroom-view/store/dealroom-view.store';
import { FieldService } from '@/api/fields/services/field.service';
import { IField } from '@/dealroom/fields/types/field';
import { RootState } from '@/store/store';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { IDocumentRevision } from '@/types/document-revision';
import { translate } from '@/helpers/translation.helper';
import {
  editorContentNamespace,
  EEditorContentAction,
} from '@/editor/editor-block/store/editor-content.constants';
import { DealroomHelper } from '@/dealroom/helpers/dealroom.helper';

export const actionLoadDocument = 'loadDocument';
export const actionLoadDocumentSuccess = 'loadDocumentSuccess';
export const actionLoadDocumentFailed = 'loadDocumentFailed';

export const actionLoadDocumentRevisions = 'loadDocumentRevisions';
export const actionLoadDocumentRevisionsSuccess = 'loadDocumentRevisionsSuccess';
export const actionLoadDocumentRevisionsFailed = 'loadDocumentRevisionsFailed';

export const actionSetDocumentRevisionStatus = 'setDocumentRevisionStatus';

export const actionUpdatePage = 'updatePage';

export const actionLoadFields = 'loadFields';
export const actionLoadFieldsSuccess = 'loadFieldsSuccess';
export const actionLoadFieldsFailed = 'loadFieldsFailed';

export const actionSetPageWidth = 'setPageWidth';

export const actionloadPageThumbUrl = 'loadPageThumbUrl';

export const actionPublishDocument = 'publishDocument';
export const actionPublishDocumentSuccess = 'publishDocumentSuccess';
export const actionPublishDocumentFailed = 'publishDocumentFailed';

export const actionDiscardChanges = 'discardChanges';
export const actionDiscardChangesSuccess = 'discardChangesSuccess';
export const actionDiscardChangesFailed = 'discardChangesFailed';

export const actionEditorUpdateFailed = 'editorUpdateFailed';

export const actions: ActionTree<IDealroomViewState, RootState> = {
  async [actionLoadDocument]({ commit, dispatch }, documentId: string) {
    commit(mutationSetLoading, true);
    try {
      const document: IDocument = await DocumentService.loadDocument(documentId);
      dispatch(actionLoadDocumentSuccess, document);
    } catch (err) {
      this.dispatch(actionLoadDocumentFailed, err);
    }
  },
  async [actionLoadDocumentSuccess]({ dispatch, commit }, document: IDocument) {
    commit(mutationSetDocument, document);
    commit(mutationSetLoading, false);
    commit(mutationSetLoaded, true);
    dispatch(actionLoadFields, document.id);
  },
  async [actionLoadDocumentFailed]({ commit }) {
    commit(mutationSetLoading, false);
    commit(mutationSetLoaded, false);
  },
  async [actionLoadDocumentRevisions]({ commit, dispatch }, documentId: string) {
    commit(mutationSetRevisionsLoading, true);
    try {
      const documentRevisions: IDocumentRevision[] = await DocumentService.loadDocumentRevisions(
        documentId
      );
      dispatch(actionLoadDocumentRevisionsSuccess, documentRevisions);
    } catch (err) {
      this.dispatch(actionLoadDocumentRevisionsFailed);
    }
  },
  [actionLoadDocumentRevisionsSuccess]({ commit }, documentRevisions: IDocumentRevision[]) {
    commit(mutationSetRevisionsLoading, false);
    commit(mutationSetDocumentRevisions, documentRevisions);
  },
  [actionLoadDocumentRevisionsFailed]({ commit }) {
    commit(mutationSetRevisionsLoading, false);
  },
  [actionSetDocumentRevisionStatus]({ commit }, payload: ISetDocumentRevisionStatusPayload) {
    commit(mutationSetDocumentRevisionStatus, payload);
  },
  [actionUpdatePage]({ state, commit }, page: IDocumentPage) {
    commit(mutationSetPages, DealroomHelper.updatePage(state.document.pages, page));
  },
  async [actionSetPageWidth]({ commit }, pageWidth: number) {
    commit(mutationSetPageWidth, pageWidth);
  },
  async [actionPublishDocument]({ commit, dispatch, state }, payload: IPublishDocumentPayload) {
    commit(mutationSetLoading, true);
    dispatch(
      'alert/showInfoAlert',
      translate(`Updating ${state.document.name}. You will be notified when it's done`),
      {
        root: true,
      }
    );
    try {
      await DocumentService.publishDocument(payload);
      await dispatch(actionLoadDocument, payload.documentId);
      dispatch(actionPublishDocumentSuccess, payload.documentId);
    } catch (err) {
      dispatch(actionPublishDocumentFailed, err);
    }
  },
  [actionPublishDocumentSuccess]({ dispatch, commit, state }) {
    commit(mutationSetLoading, false);
    commit(mutationSetLoaded, true);
    dispatch(
      'alert/showSuccessAlert',
      translate(
        `${state.document.name} is updated. All recipients have been notified of your changes`
      ),
      {
        root: true,
      }
    );
  },
  [actionPublishDocumentFailed]({ state, commit, dispatch }) {
    commit(mutationSetLoading, false);
    commit(mutationSetLoaded, true);
    dispatch('alert/showDangerAlert', translate(`${state.document.name} could not be updated`), {
      root: true,
    });
  },
  async [actionDiscardChanges]({ commit, dispatch, state }, documentId: string) {
    commit(mutationSetLoading, true);
    dispatch('alert/showInfoAlert', translate(`Discarding changes to ${state.document.name}`), {
      root: true,
    });
    try {
      await DocumentService.discardChanges(documentId);
      dispatch(
        `${editorContentNamespace}/${EEditorContentAction.ResetBlocks}`,
        {},
        {
          root: true,
        }
      );
      await dispatch(actionLoadDocument, documentId);
      dispatch(actionDiscardChangesSuccess, documentId);
    } catch (err) {
      dispatch(actionDiscardChangesFailed, err);
    }
  },
  [actionDiscardChangesSuccess]({ dispatch, commit, state }) {
    commit(mutationSetLoading, false);
    dispatch(
      'alert/showSuccessAlert',
      translate(`Changes to ${state.document.name} have been discarded`),
      {
        root: true,
      }
    );
  },
  [actionDiscardChangesFailed]({ commit, dispatch, state }) {
    commit(mutationSetLoading, false);
    dispatch(
      'alert/showDangerAlert',
      translate(`Changes to ${state.document.name} could not be discarded`),
      {
        root: true,
      }
    );
  },
  [actionEditorUpdateFailed]({ commit, dispatch, state }) {
    commit(mutationSetUpdateFailed);
    dispatch(actionDiscardChanges, state.document.id);
  },
  async [actionLoadFields]({ dispatch }, documentId: string) {
    try {
      const fields: IField[] = await FieldService.load(documentId);
      dispatch(actionLoadFieldsSuccess, fields);
    } catch (error) {
      dispatch(actionLoadFieldsFailed, error);
    }
  },
  async [actionLoadFieldsSuccess]({ commit }, fields: IField[]) {
    commit(mutationSetFields, fields);
  },
  async [actionloadPageThumbUrl]({ commit, state }, pageId: string) {
    const { thumbUrl }: Partial<IDocumentPage> = await DocumentService.loadPageThumbUrl(
      pageId,
      state.document.id
    );
    const pages: IDocumentPage[] = state.document.pages.map((page: IDocumentPage) =>
      page.id === pageId
        ? {
            ...page,
            thumbUrl,
          }
        : page
    );
    commit(mutationSetPages, pages);
  },
};
